export interface InjectionEvalScenario {
	id: string;
	title: string;
	category: "locating" | "decision" | "outcome" | "troubleshooting" | "continuation";
	prompt: string;
	expectedModes?: string[];
	expectedPrimary: string[];
	expectedAntiSignals: string[];
}

export interface InjectionEvalScenarioPack {
	id: string;
	title: string;
	description: string;
	scenarios: InjectionEvalScenario[];
}

export function getInjectionEvalScenarioByPrompt(prompt: string): InjectionEvalScenario | null {
	const normalized = prompt.trim().toLowerCase();
	for (const pack of INJECTION_EVAL_SCENARIO_PACKS) {
		for (const scenario of pack.scenarios) {
			if (scenario.prompt.trim().toLowerCase() === normalized) return scenario;
		}
	}
	return null;
}

export const INJECTION_EVAL_SCENARIO_PACKS: InjectionEvalScenarioPack[] = [
	{
		id: "track3-core",
		title: "Track 3 core injection scenarios",
		description:
			"Core scenarios covering decision continuity, recap control, locating value, and workstream continuation.",
		scenarios: [
			{
				id: "decision-recap-weighting",
				title: "Recap weighting decision continuity",
				category: "decision",
				prompt: "what did we decide about recap weighting",
				expectedModes: ["default", "recall"],
				expectedPrimary: ["decision", "discovery", "durable"],
				expectedAntiSignals: ["generic recap sludge", "wrong-thread summary"],
			},
			{
				id: "topic-memory-retrieval",
				title: "Memory retrieval topic quality",
				category: "outcome",
				prompt: "memory retrieval issues",
				expectedModes: ["default"],
				expectedPrimary: ["discovery", "bugfix", "decision"],
				expectedAntiSignals: ["recap takeover", "unmapped recap"],
			},
			{
				id: "sessionization-summary-emission",
				title: "Sessionization summary emission policy",
				category: "decision",
				prompt: "sessionization summary emission",
				expectedModes: ["recall", "default"],
				expectedPrimary: ["decision", "durable policy memory"],
				expectedAntiSignals: ["explicit recap bias", "summary-first sludge"],
			},
			{
				id: "oauth-recurrence",
				title: "OAuth recurrence troubleshooting continuity",
				category: "troubleshooting",
				prompt: "what did we decide last time about oauth",
				expectedModes: ["recall"],
				expectedPrimary: ["decision", "bugfix", "troubleshooting outcome"],
				expectedAntiSignals: ["wrong-thread latest summary", "recap-only output"],
			},
			{
				id: "track3-continuation",
				title: "Track 3 continuation context",
				category: "continuation",
				prompt: "what should we do next about recap policy",
				expectedModes: ["task", "default"],
				expectedPrimary: ["decision", "next-step context", "durable workstream context"],
				expectedAntiSignals: ["generic summary", "administrative chatter"],
			},
		],
	},
	{
		id: "track3-explicit-recap",
		title: "Explicit recap quality scenarios",
		description: "Scenarios that verify recap remains useful when the user explicitly asks for it.",
		scenarios: [
			{
				id: "summary-of-oauth",
				title: "Explicit recap request for OAuth work",
				category: "decision",
				prompt: "summary of oauth",
				expectedModes: ["recall"],
				expectedPrimary: ["session_summary", "recap"],
				expectedAntiSignals: ["missing summary intent", "topic-only refusal"],
			},
			{
				id: "catch-up-retrieval",
				title: "Explicit catch-up request",
				category: "continuation",
				prompt: "catch me up on memory retrieval work",
				expectedModes: ["recall"],
				expectedPrimary: ["session_summary", "recap", "orientation context"],
				expectedAntiSignals: ["totally summary-free output"],
			},
		],
	},
	{
		id: "dual-artifact-v1",
		title: "Dual-artifact memory model scenarios",
		description:
			"Measurable-now probes for summary domination, telemetry intrusion, explicit recap quality, and derived-fact-like precision before first-class derived_fact artifacts exist.",
		scenarios: [
			{
				id: "summary-domination-topic",
				title: "Topical default retrieval prefers durable facts over summaries",
				category: "decision",
				prompt: "dual artifact durable policy",
				expectedModes: ["default", "recall"],
				expectedPrimary: ["durable", "decision", "derived fact", "implementation contract"],
				expectedAntiSignals: ["recap takeover", "generic summary", "administrative chatter"],
			},
			{
				id: "telemetry-task-intrusion",
				title: "Task retrieval avoids review and validation telemetry",
				category: "continuation",
				prompt: "what should we do next about dual artifact memory",
				expectedModes: ["task", "default"],
				expectedPrimary: ["next-step context", "durable workstream context", "derived fact"],
				expectedAntiSignals: ["administrative chatter", "review telemetry", "validation telemetry"],
			},
			{
				id: "telemetry-debug-intrusion",
				title: "Debug retrieval prefers root-cause facts over telemetry",
				category: "troubleshooting",
				prompt: "why did memory extraction miss implementation contracts",
				expectedModes: ["default", "recall"],
				expectedPrimary: ["bugfix", "discovery", "root cause", "derived fact"],
				expectedAntiSignals: ["review telemetry", "validation telemetry", "bootstrap telemetry"],
			},
			{
				id: "explicit-dual-artifact-recap",
				title: "Explicit recap remains summary-capable",
				category: "continuation",
				prompt: "catch me up on dual artifact memory work",
				expectedModes: ["recall"],
				expectedPrimary: ["session_summary", "recap", "orientation context"],
				expectedAntiSignals: ["totally summary-free output", "wrong-thread summary"],
			},
			{
				id: "derived-fact-like-contract",
				title: "Derived-fact-like precision for embedded implementation contracts",
				category: "decision",
				prompt: "what must future memory extraction preserve",
				expectedModes: ["default", "recall"],
				expectedPrimary: ["derived fact", "implementation contract", "durable"],
				expectedAntiSignals: ["recap takeover", "validation telemetry", "bootstrap telemetry"],
			},
		],
	},
];

export function getInjectionEvalScenarioPack(id: string): InjectionEvalScenarioPack | null {
	const normalized = id.trim().toLowerCase();
	return INJECTION_EVAL_SCENARIO_PACKS.find((pack) => pack.id === normalized) ?? null;
}

export function getInjectionEvalScenarioPrompts(ids: string[]): string[] {
	const prompts: string[] = [];
	const seen = new Set<string>();
	for (const id of ids) {
		const pack = getInjectionEvalScenarioPack(id);
		if (!pack) continue;
		for (const scenario of pack.scenarios) {
			if (seen.has(scenario.prompt)) continue;
			seen.add(scenario.prompt);
			prompts.push(scenario.prompt);
		}
	}
	return prompts;
}
